import { useMemo, useState } from "react";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { activeOnly } from "@/lib/users.js";
import { SearchInput } from "@/components/ui/Form.jsx";
import { Avatar, Callout } from "@/components/ui/Display.jsx";
import Tag from "@/components/ui/Tag.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { currentTeamName, idOf, memberIds } from "@/lib/teams.js";

/*
 * Manage a team's members.
 *
 * Saves the COMPLETE member list with one PATCH rather than calling the
 * add-members and remove-members endpoints. A session that both adds and removes
 * would otherwise need two requests, and a failure between them leaves the team
 * half-changed. `updateTeam` takes the full array and works out the difference
 * itself, so one request either lands or does not.
 *
 * Someone already in another team can be added — that is a MOVE, and the row
 * says which team they are being taken out of, because the consequence is
 * invisible otherwise.
 *
 * Mounted only while open (see Teams.jsx), so the staged selection starts fresh
 * each time rather than being reset by an effect — Drawer renders null when
 * closed anyway, so there is no exit animation to preserve.
 */
export default function MembersDrawer({ team, users = [], teams = [], onClose, onSave, saving }) {
    // Staged locally so the whole session is one save, and Cancel really cancels.
    const [selected, setSelected] = useState(() => new Set(memberIds(team)));
    const [search, setSearch] = useState("");

    const managerId = idOf(team?.manager);

    const { members, addable } = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = (u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q);

        return {
            members: users.filter((u) => selected.has(u._id)).sort((a, b) => a.name.localeCompare(b.name)),
            // Active only — adding a deactivated account to a team creates a
            // member who can never be tracked. Already-selected members are
            // listed above regardless, so an existing one is never hidden.
            addable: activeOnly(users)
                .filter((u) => !selected.has(u._id) && matches(u))
                .sort((a, b) => a.name.localeCompare(b.name)),
        };
    }, [users, selected, search]);

    const toggle = (id) =>
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const original = new Set(memberIds(team));
    const added = [...selected].filter((id) => !original.has(id));
    const removed = [...original].filter((id) => !selected.has(id));
    const dirty = added.length > 0 || removed.length > 0;

    // Removing the manager is allowed but worth saying out loud: the card then
    // shows "Not set" and nobody can manage the team until one is assigned.
    const removingManager = managerId && removed.includes(managerId);

    return (
        <Drawer
            open
            onClose={onClose}
            title={team ? `${team.name} members` : "Members"}
            subtitle={`${selected.size} ${selected.size === 1 ? "member" : "members"} selected`}
            size="lg"
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => onSave([...selected])}
                        disabled={!dirty}
                        loading={saving}
                        data-save-members
                    >
                        {dirty
                            ? `Save ${added.length ? `+${added.length}` : ""}${added.length && removed.length ? " / " : ""}${removed.length ? `−${removed.length}` : ""}`
                            : "Save changes"}
                    </Button>
                </>
            }
        >
            <div className="space-y-5">
                {removingManager && (
                    <Callout tone="warning">
                        You are removing this team's manager. The team will have none until you
                        assign one from <strong className="font-semibold">Edit team</strong>.
                    </Callout>
                )}

                {/* ---- Current members ---- */}
                <section>
                    <h3 className="mb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                        In this team
                    </h3>

                    <div className="rounded-lg border border-slate-200 dark:border-slate-800">
                        {members.length ? (
                            members.map((user) => (
                                <div
                                    key={user._id}
                                    data-member={user._id}
                                    className="flex min-w-0 items-center gap-3 border-b border-slate-100 px-3.5 py-2.5 last:border-0 dark:border-slate-800"
                                >
                                    <Avatar name={user.name} size="sm" />
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-1.5">
                                            <span className="truncate text-[13px] font-medium">
                                                {user.name}
                                            </span>
                                            {user._id === managerId && <Tag tone="brand">Manager</Tag>}
                                            {!user.isActive && <Tag tone="slate">Inactive</Tag>}
                                        </span>
                                        <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                            {user.email}
                                        </span>
                                    </span>

                                    <Button
                                        variant="icon"
                                        aria-label={`Remove ${user.name} from this team`}
                                        onClick={() => toggle(user._id)}
                                        data-remove={user._id}
                                        className="hover:text-rose-600 dark:hover:text-rose-400"
                                    >
                                        <Icon name="xCircle" className="size-4" />
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <p className="px-3.5 py-6 text-center text-[13px] text-slate-400 dark:text-slate-500">
                                No members in this team yet
                            </p>
                        )}
                    </div>
                </section>

                {/* ---- Everyone else ---- */}
                <section>
                    <div className="mb-2 flex items-center gap-3">
                        <h3 className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                            Add people
                        </h3>
                        <SearchInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name or email…"
                            aria-label="Search people to add"
                            className="ml-auto w-56"
                        />
                    </div>

                    <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
                        {addable.length ? (
                            addable.map((user) => {
                                const other = currentTeamName(user, teams);
                                return (
                                    <label
                                        key={user._id}
                                        data-addable={user._id}
                                        className="flex min-w-0 cursor-pointer items-center gap-3 border-b border-slate-100 px-3.5 py-2.5 transition last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={false}
                                            onChange={() => toggle(user._id)}
                                            className="size-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-slate-600"
                                        />
                                        <Avatar name={user.name} size="sm" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[13px] font-medium">
                                                {user.name}
                                            </span>
                                            <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                                {user.email}
                                            </span>
                                        </span>

                                        <span className="flex shrink-0 items-center gap-2">
                                            {!user.isActive && <Tag tone="slate">Inactive</Tag>}
                                            {other ? (
                                                <Tag tone="amber">in {other}</Tag>
                                            ) : (
                                                <Tag tone="emerald">Free</Tag>
                                            )}
                                        </span>
                                    </label>
                                );
                            })
                        ) : (
                            <p className="px-3.5 py-6 text-center text-[13px] text-slate-400 dark:text-slate-500">
                                {search ? `Nobody matches “${search}”` : "Everyone is already in this team"}
                            </p>
                        )}
                    </div>

                    <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500">
                        Adding somebody already in another team MOVES them — a person belongs to one
                        team at a time.
                    </p>
                </section>
            </div>
        </Drawer>
    );
}
